import { useQuery } from '@apollo/client';
import { GetRiskDescendantIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useRemoveRiskFromEnterpriseRisk } from '@/hooks/mutations/enterprise-risk';
import { useRemoveResultNotification } from '@/hooks/useMutationResultNotification';

import ConfirmModal from '../confirm-modal/ConfirmModal';

interface Props {
  riskId: string;
  isVisible: boolean;
  onDismiss: () => void;
}

const RemoveFromEnterpriseRiskModal: FC<Props> = ({
  riskId,
  isVisible,
  onDismiss,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'enterpriseRisks.removeRiskFromEnterpriseRisk',
  });
  const { removeRiskFromEnterpriseRisk, loading } =
    useRemoveRiskFromEnterpriseRisk();

  // Unlinking a tier 1 risk cascades to its descendants — warn about the
  // downstream impact before the user confirms. Confirm stays disabled until
  // the descendant count has loaded so the warning cannot be skipped.
  const { data, loading: descendantsLoading } = useQuery(
    GetRiskDescendantIdsDocument,
    {
      variables: { Id: riskId },
      skip: !isVisible,
    }
  );
  const descendantCount =
    data?.risk.reduce((acc, child) => acc + 1 + child.childRisks.length, 0) ??
    0;

  const onConfirm = useRemoveResultNotification({
    entityName: t('notification_entity'),
    asyncAction: async () => {
      await removeRiskFromEnterpriseRisk(riskId);
      onDismiss();

      return true;
    },
  });

  return (
    <ConfirmModal
      isVisible={isVisible}
      onConfirm={onConfirm}
      onDismiss={onDismiss}
      header={t('modal_title')}
      loading={loading || descendantsLoading}
    >
      {t('confirm_message')}{' '}
      {descendantCount > 0 &&
        t('descendant_warning', { count: descendantCount })}
    </ConfirmModal>
  );
};

export default RemoveFromEnterpriseRiskModal;
